import React, { useEffect } from 'react';
import styled from 'styled-components';
import {
  ServerIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  PuzzlePieceIcon,
  ArrowPathRoundedSquareIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const expertiseDomains = [
  {
    icon: ArrowPathRoundedSquareIcon,
    title: 'End-to-end Feature Ownership',
    description:
      'Carrying a feature from backend domain logic through API, web and mobile UI, infrastructure.',
    tags: ['Kotlin', 'Go', 'PHP', 'TypeScript', 'Dart', 'Terraform', 'Kubernetes'],
  },
  {
    icon: PuzzlePieceIcon,
    title: 'Complex systems with distributed components',
    description:
      'Developing complex systems with event-driven communication (internal + external)',
    tags: ['Kafka', 'RabbitMQ', 'Websockets/Webhooks', 'http/3'],
  },
  {
    icon: ShieldCheckIcon,
    title: 'Security & Authentication',
    description:
      'Multi-domain auth flows, OIDC/SSO integrations, social login, and secure flows on the auth-critical paths.',
    tags: ['OIDC', 'SSO', 'OAuth2', 'JWT', 'RBAC/ACL'],
  },
  {
    icon: CreditCardIcon,
    title: 'Financial systems',
    description:
      'Building financial and ecommerce components',
    tags: ['Payment', 'Billing', 'Integrations', 'Invoicing'],
  },
  {
    icon: ServerIcon,
    title: 'API & Platform Design',
    description:
      'Designing and exposing clean APIs (API versioning, backward compatibility via versioning)',
    tags: ['REST', 'API Design', 'OpenAPI'],
  },
  {
    icon: UserGroupIcon,
    title: 'Large Team Collaboration',
    description:
      'Working with large, cross-functional teams, following engineering best practices, contributing to guidelines and rulesets',
    tags: ['Soft skills']
  },
];

const techStack = [
  {
    layer: 'Backend',
    items: ['PHP', 'Laravel', 'Symfony', 'Kotlin', 'Micronaut', 'Go', 'API Platform', 'Doctrine', 'Eloquent', 'GORM', 'GIN'],
  },
  {
    layer: 'Frontend & Mobile',
    items: ['TypeScript', 'React', 'Dart', 'Flutter'],
  },
  {
    layer: 'Infra & Platform',
    items: ['Terraform', 'Kubernetes', 'Helm', 'Docker', 'AWS', 'Cloudflare', 'Linux', '*BSD'],
  },
  {
    layer: 'Data & Messaging',
    items: ['MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'Elasticsearch', 'Firebase'],
  },
  {
    layer: 'Auth & Identity',
    items: ['Ory Kratos', 'OIDC', 'OAuth2', 'SSO', 'JWT'],
  },
  {
    layer: 'Practices',
    items: ['TDD', 'DDD', 'SOLID', 'OWASP', 'CI/CD', 'Microservices', 'Conventional Commits'],
  },
];

const Profile = () => {
  useEffect(() => {
    document.title = 'Profile — Dmitri Malisev';
  }, []);

  return (
    <ProfileContainer>
      <Section id="about">
        <SectionLabel>About</SectionLabel>
        <SectionTitle>Who I am</SectionTitle>
        <AboutText>
          Full-stack engineer with a strong backend foundation. I enjoy working on complex
          systems, shipping features end-to-end, and pushing for performance and efficiency.
          I enjoy working on legacy systems just as much as with a new tech stack. Equally
          fluent in building everything by hand and in orchestrating multi-agent AI workstreams
          to produce results.
        </AboutText>
        <AboutText>
          I value disciplined engineering: conventional commits, tests written alongside
          features, taking code review seriously, iterating based on feedback and caring about efficient code.
        </AboutText>
        <AboutText>
          My path started in the early 2000s building PCs and running a 20+ node neighborhood
          LAN with my friends, configuring and maintaining a local server powered by FreeBSD where I wrote my first scripts in Perl, then built desktop apps in Delphi to interact with it.
          That hands-on curiosity eventually led to studying Computer Science and working
          professionally across networking, APIs, and SaaS platforms. That low-level instinct
          never went away: I still care a lot about what happens beneath the abstraction. 
        </AboutText>
        <AboutText>
          Based in Berlin, Germany. Currently exploring what AI can and can't do.
        </AboutText>
      </Section>

      <Divider />

      <Section id="expertise">
        <SectionLabel>Expertise</SectionLabel>
        <SectionTitle>What I work on</SectionTitle>
        <DomainGrid>
          {expertiseDomains.map(domain => (
            <DomainCard key={domain.title}>
              <DomainHeader>
                <IconWrapper>
                  <domain.icon width={18} height={18} />
                </IconWrapper>
                <DomainTitle>{domain.title}</DomainTitle>
              </DomainHeader>
              <DomainDesc>{domain.description}</DomainDesc>
              <TagRow>
                {domain.tags.map(tag => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </TagRow>
            </DomainCard>
          ))}
        </DomainGrid>
      </Section>

      <Divider />

      <Section id="skills">
        <SectionLabel>Skills</SectionLabel>
        <SectionTitle>Tech stack</SectionTitle>
        <StackTable>
          {techStack.map(row => (
            <StackRow key={row.layer}>
              <StackLayer>{row.layer}</StackLayer>
              <StackItems>
                {row.items.map(item => (
                  <StackTag key={item}>{item}</StackTag>
                ))}
              </StackItems>
            </StackRow>
          ))}
        </StackTable>
      </Section>
    </ProfileContainer>
  );
};

const ProfileContainer = styled.div`
  padding: 4rem 0;
`;

const Section = styled.section`
  scroll-margin-top: 80px;
`;

const SectionLabel = styled.p`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--accent);
  margin: 0 0 0.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.625rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 2rem;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.375rem;
  }
`;

const AboutText = styled.p`
  font-size: 1.0625rem;
  line-height: 1.8;
  color: var(--text-primary);
  max-width: 720px;
  margin: 0 0 1.25rem;

  @media (max-width: 600px) {
    font-size: 0.9375rem;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--border);
  margin: 4rem 0;
`;

const DomainGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DomainCard = styled.div`
  padding: 1.375rem 1.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-surface);
  transition: border-color 0.2s;

  &:hover {
    border-color: var(--border-accent);
  }
`;

const DomainHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 0.75rem;
`;

const IconWrapper = styled.div`
  color: var(--accent);
  flex-shrink: 0;
  display: flex;
`;

const DomainTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const DomainDesc = styled.p`
  font-size: 0.875rem;
  line-height: 1.7;
  color: var(--text-muted);
  margin: 0 0 1rem;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const Tag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid var(--border);
  border-radius: 3px;
  color: var(--text-muted);
`;

const StackTable = styled.div`
  border: 1px solid var(--border);
  border-radius: 6px;
  overflow: hidden;
`;

const StackRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StackLayer = styled.div`
  padding: 0.875rem 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
  background: var(--bg-surface);
  border-right: 1px solid var(--border);
  display: flex;
  align-items: center;

  @media (max-width: 600px) {
    border-right: none;
    border-bottom: 1px solid var(--border);
    padding: 0.625rem 1rem;
    background: var(--bg-surface-2);
  }
`;

const StackItems = styled.div`
  padding: 0.75rem 1.25rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  background: var(--bg-primary);

  @media (max-width: 600px) {
    padding: 0.75rem 1rem;
  }
`;

const StackTag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  padding: 0.2rem 0.55rem;
  border: 1px solid var(--border);
  border-radius: 3px;
  color: var(--text-muted);
  background: var(--bg-surface);
`;

export default Profile;
